import axios from 'axios'
import cacheService from './CacheService'

// Base URL for API requests
const baseUrl = process.env.REACT_APP_BACKEND_URL

/**
 * API service for making HTTP requests
 */
class ApiService {
  /**
   * Constructor
   */
  constructor() {
    // Define supported entity types and their endpoints
    this.entityConfig = {
      articles: {
        searchEndpoint: '/api/v1/articles/search',
        getBySlugEndpoint: '/api/v1/articles/slug',
        cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
      },
      chapters: {
        searchEndpoint: '/api/v1/chapters/search',
        getBySlugEndpoint: '/api/v1/chapters/slug',
        cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
      },
      decisions: {
        searchEndpoint: '/api/v1/decisions/search',
        getBySlugEndpoint: '/api/v1/decisions/slug',
        cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
      },
      guidelines: {
        searchEndpoint: '/api/v1/guidelines/search',
        getBySlugEndpoint: '/api/v1/guidelines/slug',
        cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
      },
      taxTreaties: {
        searchEndpoint: '/api/v1/tax-treaties/search',
        getBySlugEndpoint: '/api/v1/tax-treaties/slug',
        cacheTTL: 24 * 60 * 60 * 1000, // 24 hours
      },
      // server side pagination
      globalSearch: {
        searchEndpoint: '/api/v1/global-search/',
        cacheTTL: 15 * 60 * 1000, // 15 minutes
      },
      // client side pagination: fetch all the documents
      globalSearchCSP: {
        searchEndpoint: '/api/v1/global-search/all',
        cacheTTL: 15 * 60 * 1000, // 15 minutes
      },
    }

    // Create axios instance with default config
    this.axios = axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add response interceptor for error handling
    this.axios.interceptors.response.use(
      (response) => response,
      (error) => this.handleError(error)
    )
  }

  /**
   * Handle API errors
   *
   * @param {Error} error Axios error
   * @returns {Promise<Error>} Rejected promise with error
   */
  handleError(error) {
    let errorMessage = 'An unexpected error occurred'

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const statusCode = error.response.status

      switch (statusCode) {
        case 400:
          errorMessage =
            'Bad request: ' +
            (error.response.data?.message || 'Please check your input')
          break
        case 401:
          errorMessage = 'Unauthorized: Please log in again'
          break
        case 403:
          errorMessage =
            'Forbidden: You do not have permission to access this resource'
          break
        case 404:
          errorMessage = 'Not found: The requested resource does not exist'
          break
        case 500:
          errorMessage = 'Server error: Please try again later'
          break
        default:
          errorMessage = `Error ${statusCode}: ${
            error.response.data?.message || 'Something went wrong'
          }`
      }

      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      })
    } else if (error.request) {
      // The request was made but no response was received
      errorMessage = 'Network error: Please check your connection'
      console.error('Network Error:', error.request)
    } else {
      // Something happened in setting up the request
      errorMessage = 'Request error: ' + error.message
      console.error('Request Error:', error.message)
    }

    // Enhance error object with user-friendly message
    error.userMessage = errorMessage

    return Promise.reject(error)
  }

  /**
   * Generic method to make a GET request to any endpoint
   *
   * @param {string} endpoint API endpoint
   * @param {Object} params Query parameters
   * @param {Object} options Cache options
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint, { params })
        return response.data
      },
      endpoint,
      params,
      options
    )
  }

  /**
   * Generic method to make a POST request to any endpoint
   *
   * @param {string} endpoint API endpoint
   * @param {Object} data Request body
   * @param {Object} options Additional axios options
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data = {}, options = {}) {
    try {
      const response = await this.axios.post(endpoint, data, options)
      return response.data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get countries with laws
   *
   * @param {Object} options Cache options
   * @returns {Promise<Array>} Countries data
   */
  async getCountriesWithLaws(options = {}) {
    const endpoint = '/api/v1/countries/with-laws'

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint)
        return response.data
      },
      endpoint,
      {},
      {
        // Countries data changes infrequently, so cache for a longer time
        ttl: 60 * 60 * 1000, // 1 hour
        ...options,
      }
    )
  }

  /**
   * Perform a global search across multiple entity types
   *
   * @param {Object} params Search parameters including country, lawShortName, query, articlePage, decisionPage, guidePage
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Combined search results for all entity types
   */
  async globalSearch(params, options = {}) {
    const endpoint = this.entityConfig.globalSearch.searchEndpoint

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint, { params })
        return response.data
      },
      endpoint,
      params,
      {
        // Global search results should have a shorter cache time
        ttl: this.entityConfig.globalSearch.cacheTTL,
        ...options,
      }
    )
  }
  async globalSearchCSP(params, options = {}) {
    const endpoint = this.entityConfig.globalSearchCSP.searchEndpoint

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint, { params })
        return response.data
      },
      endpoint,
      params,
      {
        // Global search results should have a shorter cache time
        ttl: this.entityConfig.globalSearch.cacheTTL,
        ...options,
      }
    )
  }

  /**
   * Generic method to search entities
   *
   * @param {string} entityType Type of entity to search (articles, chapters, decisions, guidelines, taxTreaties)
   * @param {Object} params Search parameters
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Search results
   */
  async searchEntities(entityType, params, options = {}) {
    // Check if entity type is supported
    if (!this.entityConfig[entityType]) {
      throw new Error(`Unsupported entity type: ${entityType}`)
    }

    const endpoint = this.entityConfig[entityType].searchEndpoint

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint, { params })
        return response.data
      },
      endpoint,
      params,
      options
    )
  }

  /**
   * Generic method to get entity by slug
   *
   * @param {string} entityType Type of entity to get (articles, chapters, decisions, guidelines, taxTreaties)
   * @param {string} slug Entity slug
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Entity data
   */
  async getEntityBySlug(entityType, slug, options = {}) {
    // Check if entity type is supported
    if (!this.entityConfig[entityType]) {
      throw new Error(`Unsupported entity type: ${entityType}`)
    }

    const config = this.entityConfig[entityType]
    const endpoint = `${config.getBySlugEndpoint}/${slug}`

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint)
        return response.data
      },
      endpoint,
      {},
      {
        // Content likely won't change often, so cache for longer
        ttl: config.cacheTTL,
        ...options,
      }
    )
  }

  /**
   * Get partner countries for tax treaties
   *
   * @param {string} primaryCountry The primary country code/slug
   * @param {Object} options Cache options
   * @returns {Promise<Array>} Partner countries data
   */
  async getTaxTreatyPartnerCountries(primaryCountry, options = {}) {
    const endpoint = `/api/v1/tax-treaties/partner-countries/${primaryCountry}`

    return cacheService.getData(
      async () => {
        const response = await this.axios.get(endpoint)
        return response.data
      },
      endpoint,
      {},
      {
        // Partner country data doesn't change often, so cache for a longer time
        ttl: 60 * 60 * 1000, // 1 hour
        ...options,
      }
    )
  }

  // Maintain backwards compatibility with the existing API
  async searchArticles(params, options = {}) {
    return this.searchEntities('articles', params, options)
  }

  async searchChapters(params, options = {}) {
    return this.searchEntities('chapters', params, options)
  }

  async searchDecisions(params, options = {}) {
    return this.searchEntities('decisions', params, options)
  }

  async searchGuidelines(params, options = {}) {
    return this.searchEntities('guidelines', params, options)
  }

  async searchTaxTreaties(params, options = {}) {
    return this.searchEntities('taxTreaties', params, options)
  }

  async getArticleBySlug(slug, options = {}) {
    return this.getEntityBySlug('articles', slug, options)
  }

  async getChaptersBySlug(slug, options = {}) {
    return this.getEntityBySlug('chapters', slug, options)
  }

  async getDecisionBySlug(slug, options = {}) {
    return this.getEntityBySlug('decisions', slug, options)
  }

  async getGuidelineBySlug(slug, options = {}) {
    return this.getEntityBySlug('guidelines', slug, options)
  }

  async getTaxTreatyBySlug(slug, options = {}) {
    return this.getEntityBySlug('taxTreaties', slug, options)
  }

  /**
   * Clear all API cache
   */
  clearCache() {
    cacheService.clear()
  }

  /**
   * Clear cache for a specific endpoint
   *
   * @param {string} endpoint API endpoint
   */
  clearEndpointCache(endpoint) {
    cacheService.clearEndpoint(endpoint)
  }
}

// Export singleton instance
const apiService = new ApiService()
export default apiService
